<script setup lang="ts">
	const route = useRoute();
	const mounted = ref(false);

	onMounted(() => {
		setTimeout(() => {
			mounted.value = true;
		}, 0);
	});

	const hideCalc = computed(() => route?.query?.withoutacalc === 'true');
</script>

<template>
	<div class="hero-section" itemscope itemtype="https://schema.org/Service">
		<h1 class="hero-section__title" :class="{ 'hero-section__title--mounted': mounted }" itemprop="name">
			Авиаперевозки из Китая
		</h1>
		<div class="hero-section__description" itemprop="description">
			Хотите легко и быстро доставить свои товары из Китая? Наша компания предлагает надежные и
			эффективные решения для всех!
		</div>
		<div class="hero-section__buttons">
			<NuxtLink v-if="!hideCalc" to="/#calc" class="button">Рассчитать стоимость</NuxtLink>

			<NuxtLink
				to="/#stage"
				class="button button--white"
				aria-label="Узнать подробнее о авиаперевозках из Китая"
			>
				Узнать подробнее
				<Icon name="local:arrow-up-right" aria-hidden="true" />
			</NuxtLink>
		</div>
		<NuxtImg
			src="/images/bg-for-plane.jpg"
			alt="Голубое небо с облаками для фона авиаперевозок"
			sizes="720px xs:1020px md:1200px xl:1200px"
			densities="x1 x2"
			format="webp"
			preload
			class="hero-section__bg-image"
		/>
		<NuxtImg
			src="/images/plane.png"
			alt="Летящий самолет для авиаперевозок из Китая"
			sizes="933px xs:933px xl:1765px"
			densities="x1 x2"
			format="webp"
			preload
			class="hero-section__overflow-image"
			:class="{ 'hero-section__overflow-image--mounted': mounted }"
		/>

		<meta itemprop="serviceType" content="Авиа доставка" />
		<meta itemprop="areaServed" content="Russia" />
		<meta itemprop="areaServed" content="China" />

		<div itemprop="provider" itemscope itemtype="https://schema.org/Organization">
			<meta itemprop="name" content="ООО «АГЕНТСТВО ПРАВИЛЬНЫХ ПЕРЕВОЗОК»" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
	.hero-section {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		height: 679px;
		width: 100%;
		padding: 116px 15px 15px;
	}
	.hero-section__title {
		display: block;
		height: 104px;
		width: fit-content;
		color: var(--color-tertiary);
		font-size: var(--font-size-title-large);
		font-weight: 700;
		margin-bottom: 223px;
		transition: transform ease-out 1s;
		transform: translateY(-20px);
		&.hero-section__title--mounted {
			transform: translateY(0px);
		}
	}
	.hero-section__description {
		max-width: 460px;
		margin-bottom: 15px;
		flex: 1 0 auto;
	}
	.hero-section__buttons {
		display: grid;
		gap: 15px;
		grid-auto-flow: row;
		width: 100%;
		.button {
			&:hover {
				box-shadow: var(--box-shadow);
			}
		}
	}
	.hero-section__bg-image {
		z-index: -1;
		display: block;
		position: absolute;
		content: '';
		border-radius: 32px;
		top: 0px;
		bottom: 0px;
		right: 0px;
		left: 0px;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
	.hero-section__overflow-image {
		z-index: -1;
		display: block;
		position: absolute;
		content: '';
		object-fit: cover;
		top: 0px;
		left: 50%;
		width: 933px;
		height: 622px;
		max-block-size: none;
		max-inline-size: none;
		transform: translate(-455px, 25px);
		transition: transform ease-out 1s;
		transition: all ease-out 1s;
		&.hero-section__overflow-image--mounted {
			transform: translate(-465px, 10px);
		}
	}

	@media (min-width: 640px) {
		.hero-section__description {
			flex: 0 0 auto;
		}
		.hero-section__buttons {
			display: grid;
			gap: 15px;
			grid-auto-flow: column;
			width: fit-content;
		}
	}

	@media (min-width: 1240px) {
		.hero-section {
			align-items: flex-start;
			position: relative;
			height: 685px;
			width: 100%;
			padding: 170px 70px 70px;
		}
		.hero-section__title {
			display: block;
			height: auto;
			width: 612px;
			color: var(--color-tertiary);
			font-size: var(--font-size-title-large);
			font-weight: 700;
			margin-bottom: 17px;
			transition: transform ease-out 1s;
			transform: translateY(-20px);
			&.hero-section__title--mounted {
				transform: translateY(0px);
			}
		}
		.hero-section__description {
			max-width: none;
			width: 554px;
			margin-bottom: 60px;
			font-size: 20px;
			flex: 0 0 auto;
		}
		.hero-section__buttons {
			display: grid;
			gap: 15px;
			grid-auto-flow: column;
			width: fit-content;
			.button {
				&:hover {
					box-shadow: var(--box-shadow);
				}
			}
		}
		.hero-section__bg-image {
			z-index: -1;
			display: block;
			position: absolute;
			content: '';
			border-radius: 50px;
			top: 0px;
			bottom: 0px;
			right: 0px;
			left: 0px;
			object-fit: cover;
			object-position: 0px -80px;
		}
		.hero-section__overflow-image {
			z-index: -1;
			display: block;
			position: absolute;
			content: '';
			top: 0px;
			left: 0px;
			width: 1765px;
			height: 884px;
			max-block-size: none;
			max-inline-size: none;
			object-fit: cover;
			transform: translate(25px, -32px);
			&.hero-section__overflow-image--mounted {
				transform: translate(0px, -50px);
			}
		}
	}
</style>
